import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { addressString } from '@core/util/address';
import { Address } from '@models/address';
import {
	BusinessAddress,
	BusinessUser,
	BusinessUserRole,
} from '@models/businesses';
import {
	Encumbrance,
	EncumbranceActionType,
	EncumbranceType,
	MortgagePriorities,
} from '@models/encumbrance';
import {
	MatterAdjustmentDepositRecordCalculated,
	MatterBusinessUser,
	MatterFileType,
	MatterStatus,
	MatterPropertyType,
	Appointment,
	Keys,
	KeysPossession,
	ActingForType,
	PropertyStatus,
	MatterLRO,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterDispatch } from '@state/matter';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { DebtsState } from '@state/matter/debts';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { environment } from 'src/environments/environment';
import { EditMatterDetailsComponent } from '../edit-matter-details/edit-matter-details.component';
import { KeyDatesModalComponent } from '../key-dates-modal/key-dates-modal.component';
import { KeysModalComponent } from '../keys-modal/keys-modal.component';
import { ActingForDescription } from '@core/util/matter';
import { getEncumbranceNameByType } from '@core/util/encumbrance';
import { getGoogleMapUrlFromAddress } from '@core/util/google';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';
import { Jurisdiction } from '@models/jurisdictions';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { LeapLawyerClerkService } from 'src/app/public/leap/leap-lawyer-clerk-service';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { TestHooks } from './right-side.hooks';

const MatterStatusOptions = [
	{ label: 'Active', value: 'active' },
	{ label: 'Inactive', value: 'inactive' },
];

const PossessionLabels = {
	[KeysPossession.VENDOR]: 'Vendor',
	[KeysPossession.VENDOR_BROKER]: "Vendor's broker",
	[KeysPossession.VENDOR_LAWYER]: "Vendor's lawyer",
	[KeysPossession.LOCKBOX]: 'Lockbox',
};

@UntilDestroy()
@Component({
	selector: 'app-matter-home-right-side',
	templateUrl: './right-side.component.html',
	styleUrls: ['./right-side.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class RightSideComponent implements OnInit {
	testHooks = TestHooks;
	public roles = BusinessUserRole;
	public ICON = ICON;
	public address: Address;
	public addressString: string;
	public locationMapUrl: string;
	public matterStatusOptions = MatterStatusOptions;
	public mortgagePriorities = MortgagePriorities;
	public encumbranceTypes = EncumbranceType;
	public encumbranceActions = EncumbranceActionType;
	public possessionLabels = PossessionLabels;
	public keysPossessions = KeysPossession;
	public actingForDescription = ActingForDescription;
	public fileTypes = MatterFileType;
	public showRequisitionDate: boolean;

	@Select(SelectedMatter.address) selectedAddress$: Observable<Address>;
	@Select(MatterMortgagesState.checkForLLCorSyncMortgages)
	checkForLLCorSyncMortgages$: Observable<boolean>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.getLeapMatterId) leapMatterId: string;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;
	@ViewSelectSnapshot(AdjustmentsSelector.depositAdjustmentCalculated)
	deposit: MatterAdjustmentDepositRecordCalculated;

	@ViewSelectSnapshot(SelectedMatter.titleSearchCompletionDate)
	titleSearchCompletionDate: string;
	@ViewSelectSnapshot(SelectedMatter.closingDate) closingDate: string;
	@ViewSelectSnapshot(SelectedMatter.createdAt) createdAt: string;
	@ViewSelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@ViewSelectSnapshot(SelectedMatter.activeAssignedClerk)
	assignedClerk: MatterBusinessUser;
	@ViewSelectSnapshot(SelectedMatter.activeAssignedLawyer)
	assignedLawyer: MatterBusinessUser;
	@ViewSelectSnapshot(MatterEncumbrancesState.getSorted)
	encumbrances: Encumbrance[];
	@ViewSelectSnapshot(DebtsState.getTotal) debtTotal: number;
	@ViewSelectSnapshot(SelectedMatter.propertyType)
	propertyType: MatterPropertyType;
	@ViewSelectSnapshot(SelectedMatter.status) status: MatterStatus;
	@ViewSelectSnapshot(SelectedMatter.propertyTypeLabel)
	propertyTypeLabel: string;
	@ViewSelectSnapshot(SelectedMatter.appointment) appointment: Appointment;
	@ViewSelectSnapshot(SelectedMatter.keys) keys: Keys;
	@ViewSelectSnapshot(SelectedMatter.office) office: BusinessAddress;
	@ViewSelectSnapshot(SelectedMatter.matterName) name: string;
	@ViewSelectSnapshot(SelectedMatter.matterNumber) number: string;
	@ViewSelectSnapshot(SelectedMatter.actingFor) actingFor: ActingForType;
	@ViewSelectSnapshot(SelectedMatter.matterAccountingNumber)
	accountingNumber: ActingForType;
	@ViewSelectSnapshot(SelectedMatter.occupancyDate) occupancyDate: string;
	@ViewSelectSnapshot(SelectedMatter.LRO) lro: MatterLRO;
	@ViewSelectSnapshot(MatterJurisdictionSelectors.getPrimary)
	primaryJurisdiction: Jurisdiction;

	constructor(
		private navService: NavigationService,
		private matterDispatch: MatterDispatch,
		private dialog: MatDialog,
		private analytics: AnalyticsService,
		private leapLawyerClerkService: LeapLawyerClerkService,
		private drawerService: DrawerService,
	) {}

	ngOnInit(): void {
		this.selectedAddress$
			.pipe(untilDestroyed(this))
			.subscribe((_add: Address) => {
				if (_add) {
					this.addressString = addressString(_add);
					this.address = _add;
					const centerPoint = `${_add.street}%2C${_add.city}%2C${_add.province}%2C${_add.country}%2C${_add.postcode}`;
					const base = `https://maps.googleapis.com/maps/api/staticmap?center=${centerPoint}&markers=color:red`;

					this.locationMapUrl = _add?._geoloc?.lat
						? `${base}%7C${_add?._geoloc?.lat},${_add?._geoloc?.lng}&size=600x250&zoom=17&key=${environment.firebase.apiKey}`
						: `${base}%7C${centerPoint}&size=600x250&zoom=17&key=${environment.firebase.apiKey}`;
				}
			});

		this.showRequisitionDate = this.fileType !== MatterFileType.REFINANCE;
	}

	trackByEncumbranceId(_index: number, item: Encumbrance) {
		return item.id;
	}

	goToBillsAndDebts() {
		this.analytics.logEvent(
			AnalyticsEvent.OVERVIEW_IN_FILE_PAYMENTS_BILLS_DEBTS_CLICK,
			{ matterId: this.matterId },
		);
		this.navService.goToMatter_Debts(this.matterId, {
			'back-string': 'overview',
		});
	}

	goToEncumbrance(encumbrance) {
		this.analytics.logEvent(
			AnalyticsEvent.OVERVIEW_IN_FILE_PAYMENTS_ENCUMBRANCES_CLICK,
			{ matterId: this.matterId },
		);
		this.navService.goToMatter_Mortgages_Encumbrances_Encumbrance(
			this.matterId,
			encumbrance.id,
			{
				'back-string': 'overview',
			},
		);
	}

	goToPropertyDetails() {
		this.navService.goToMatter_Property(this.matterId, {
			'back-string': 'overview',
		});
	}

	setClerk(clerk: BusinessUser): void {
		if (!clerk?.id) {
			this.resetClerk();
			return;
		}

		this.matterDispatch.SetAssignedClerk(
			this.matterId,
			clerk.id,
			clerk.firstName,
			clerk.lastName,
		);
		if (this.isLeapMatter) {
			this.leapLawyerClerkService.syncMatterUser(this.matterId, clerk, false);
		}
	}

	resetClerk(): void {
		this.matterDispatch.SetAssignedClerk(this.matterId, '', '', '');
	}

	setLawyer(lawyer: BusinessUser): void {
		this.matterDispatch.SetAssignedLawyer(
			this.matterId,
			lawyer.id,
			lawyer.firstName,
			lawyer.lastName,
		);

		if (this.isLeapMatter) {
			this.leapLawyerClerkService.syncMatterUser(
				this.matterId,
				lawyer,
				true,
				false,
			);
		}
	}

	goToGoogleMaps() {
		const url = getGoogleMapUrlFromAddress(this.address);
		this.navService.goToRemoteUrl(url, true);
	}

	editKeyDates() {
		this.drawerService.openDrawer(KeyDatesModalComponent, {
			size: 'md',
			title: 'Key Dates',
			noStyle: true,
		});
	}

	editKeys() {
		this.dialog.open(KeysModalComponent);
	}

	editMatterDetails() {
		this.drawerService.openDrawer(EditMatterDetailsComponent, {
			size: 'sm',
			title: 'Matter Details',
			noStyle: true,
		});
	}

	public getEncumbranceName(type: EncumbranceType) {
		return getEncumbranceNameByType(type);
	}

	get shouldShowOccupancyDate() {
		return (
			this.fileType !== MatterFileType.REFINANCE &&
			this.propertyType?.status === PropertyStatus.NEW
		);
	}
}
